<template>
  <div v-if="data" class="relative h-full pb-4 bg-white">
    <p class="pt-4 pl-4 text-sm text-red-500">{{ traderErrorMessage }}</p>
    <BaseSearchDropdown
      class="relative z-50"
      :class="{ 'hidden': mode == 'view' }"
      @updateSearch="updateSearch"
      @itemSelected="handleTraderSelection"
      v-model="selectedTrader"
      :listItems="filteredTraders"
      :count="tradersCount"
      :noResultsText="$t('messages.noResultsText')" />
    <table class="relative z-0 w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('competitor.traderName')"
          mode="view"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.trader"
            mode="view"
            v-model="data.trader.name"
            color="base"
            flat
            :disabled="true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          mode="view"
          :name="$t('competitor.traderCode')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.trader"
            mode="view"
            v-model="data.trader.code"
            color="base"
            flat
            :disabled="true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          mode="view"
          :name="$t('competitor.traderTaxNumber')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.trader"
            mode="view"
            v-model="data.trader.taxNumber"
            color="base"
            flat
            :disabled="true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          mode="view"
          :name="$t('competitor.traderCity')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.trader"
            mode="view"
            v-model="data.trader.city"
            color="base"
            flat
            :disabled="true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          mode="view"
          :name="$t('competitor.traderAddress')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.trader"
            mode="view"
            v-model="data.trader.address"
            color="base"
            flat
            :disabled="true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          mode="view"
          :name="$t('competitor.traderZipCode')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.trader"
            mode="view"
            v-model="data.trader.zipCode"
            color="base"
            flat
            :disabled="true" />
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';
import { type Competitor, type Trader } from '@/types/Traders';
import type { SelectDropdownItem } from '@/types/Base';
import { getTraderById, getTradersWithConditions } from '@/services/api/traders';

defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  traderErrorMessage: string;
}>();

const data = defineModel<Competitor>();

const filteredTraders = ref<SelectDropdownItem[]>([]);
const selectedTrader = ref<SelectDropdownItem | null>(null);
const tradersCount = ref(0);

async function updateSearch(searchInput: string, page: number) {
  const traders = await getTradersWithConditions({
    page: page,
    itemsPerPage: 20,
    query: searchInput,
    sortColumn: 'id',
    sortAscending: true,
  });
  if (traders.count > 0 && traders.data) {
    if (page > 1 && filteredTraders.value)
      filteredTraders.value = filteredTraders.value.concat(
        traders.data.map((t) => ({
          value: t.id,
          name: getTraderDropdownDisplayName(t),
        })) as SelectDropdownItem[]
      );
    else
      filteredTraders.value = traders.data.map((t) => ({
        value: t.id || '',
        name: getTraderDropdownDisplayName(t),
      }));
  } else filteredTraders.value = [];
  tradersCount.value = traders.count;
}

async function handleTraderSelection(item: SelectDropdownItem) {
  selectedTrader.value = item;
  if (data.value && data.value.traderId !== item.value) {
    const trader = await getTraderById(item.value as string);
    if (trader) {
      data.value.traderId = item.value as string;
      data.value.trader = trader;
    }
  }
}

function initDropdowns() {
  if (data.value && data.value.trader && data.value.traderId) {
    selectedTrader.value = {
      value: data.value.trader.id,
      name: getTraderDropdownDisplayName(data.value.trader),
    } as SelectDropdownItem;
    if (!filteredTraders.value.find((t) => t.value == selectedTrader.value?.value))
      filteredTraders.value = [...filteredTraders.value, selectedTrader.value];
  }
}

onMounted(() => {
  initDropdowns();
});

watch(
  () => data.value,
  () => {
    initDropdowns();
  },
  { deep: true }
);

function getTraderDropdownDisplayName(trader: Partial<Trader>) {
  return `${trader.name} | ${trader.code} | ${trader.taxNumber ?? ''}`;
}
</script>
